
import { useState, useRef, useCallback, useEffect } from 'react';

interface ImageCropModalProps {
  imageFile: File;
  aspectRatio?: number; // ej: 3/4 para perfil, 16/9 para hero
  onConfirm: (croppedBlob: Blob) => void;
  onCancel: () => void;
  title?: string;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export default function ImageCropModal({
  imageFile,
  aspectRatio,
  onConfirm,
  onCancel,
  title = 'Recortar imagen',
}: ImageCropModalProps) {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageNaturalSize, setImageNaturalSize] = useState({ w: 0, h: 0 });
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => setImageSrc(e.target?.result as string);
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const initCrop = useCallback(() => {
    if (!imageRef.current || !containerRef.current) return;
    const img = imageRef.current;
    const container = containerRef.current;
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;

    const imgNatW = img.naturalWidth;
    const imgNatH = img.naturalHeight;

    // Calcular escala para que la imagen quepa en el contenedor
    const scaleX = containerW / imgNatW;
    const scaleY = containerH / imgNatH;
    const s = Math.min(scaleX, scaleY, 1);

    const dispW = imgNatW * s;
    const dispH = imgNatH * s;

    setImageNaturalSize({ w: imgNatW, h: imgNatH });
    setDisplaySize({ w: dispW, h: dispH });
    setScale(s);

    // Crop inicial: 80% del área visible
    let cropW = dispW * 0.8;
    let cropH = aspectRatio ? cropW / aspectRatio : dispH * 0.8;

    if (cropH > dispH * 0.9) {
      cropH = dispH * 0.9;
      cropW = aspectRatio ? cropH * aspectRatio : dispW * 0.8;
    }

    const cropX = (dispW - cropW) / 2;
    const cropY = (dispH - cropH) / 2;

    setCrop({ x: cropX, y: cropY, width: cropW, height: cropH });
  }, [aspectRatio]);

  const handleImageLoad = useCallback(() => {
    initCrop();
  }, [initCrop]);

  const getRelativePos = (e: React.MouseEvent | React.TouchEvent, container: HTMLElement) => {
    const rect = container.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    // Offset para centrar la imagen en el contenedor
    const offsetX = (container.clientWidth - displaySize.w) / 2;
    const offsetY = (container.clientHeight - displaySize.h) / 2;
    return {
      x: clientX - rect.left - offsetX,
      y: clientY - rect.top - offsetY,
    };
  };

  const handleMouseDown = (e: React.MouseEvent, type: 'drag' | 'resize') => {
    e.preventDefault();
    if (!containerRef.current) return;
    const pos = getRelativePos(e, containerRef.current);
    setDragStart({ x: pos.x - crop.x, y: pos.y - crop.y });
    if (type === 'drag') setDragging(true);
    else setResizing(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const offsetX = (containerRef.current.clientWidth - displaySize.w) / 2;
    const offsetY = (containerRef.current.clientHeight - displaySize.h) / 2;
    const posX = e.clientX - rect.left - offsetX;
    const posY = e.clientY - rect.top - offsetY;

    if (dragging) {
      let newX = posX - dragStart.x;
      let newY = posY - dragStart.y;
      newX = Math.max(0, Math.min(newX, displaySize.w - crop.width));
      newY = Math.max(0, Math.min(newY, displaySize.h - crop.height));
      setCrop((prev) => ({ ...prev, x: newX, y: newY }));
    }

    if (resizing) {
      let newW = posX - crop.x;
      let newH = aspectRatio ? newW / aspectRatio : posY - crop.y;

      newW = Math.max(50, Math.min(newW, displaySize.w - crop.x));
      newH = Math.max(50, Math.min(newH, displaySize.h - crop.y));

      if (aspectRatio) {
        if (crop.x + newW > displaySize.w) {
          newW = displaySize.w - crop.x;
          newH = newW / aspectRatio;
        }
        if (crop.y + newH > displaySize.h) {
          newH = displaySize.h - crop.y;
          newW = newH * aspectRatio;
        }
      }

      setCrop((prev) => ({ ...prev, width: newW, height: newH }));
    }
  }, [dragging, resizing, dragStart, crop, displaySize, aspectRatio]);

  const handleMouseUp = useCallback(() => {
    setDragging(false);
    setResizing(false);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleCrop = () => {
    if (!canvasRef.current || !imageRef.current) return;
    const canvas = canvasRef.current;

    // Convertir coordenadas de display a coordenadas naturales
    const natX = (crop.x / scale);
    const natY = (crop.y / scale);
    const natW = (crop.width / scale);
    const natH = (crop.height / scale);

    canvas.width = natW;
    canvas.height = natH;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      imageRef.current,
      natX, natY, natW, natH,
      0, 0, natW, natH
    );

    canvas.toBlob((blob) => {
      if (blob) onConfirm(blob);
    }, 'image/jpeg', 0.92);
  };

  const offsetX = displaySize.w > 0 ? `calc(50% - ${displaySize.w / 2}px)` : '0px';
  const offsetY = displaySize.h > 0 ? `calc(50% - ${displaySize.h / 2}px)` : '0px';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-cyan-500/20 rounded-lg">
              <i className="ri-crop-line text-cyan-400"></i>
            </div>
            <h3 className="text-white font-bold text-base">{title}</h3>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Instrucciones */}
        <div className="px-6 py-3 bg-cyan-500/10 border-b border-cyan-500/20">
          <p className="text-cyan-300 text-xs flex items-center gap-2">
            <i className="ri-information-line"></i>
            Arrastra el recuadro para moverlo · Arrastra la esquina inferior derecha para redimensionar
          </p>
        </div>

        {/* Canvas de recorte */}
        <div
          ref={containerRef}
          className="relative bg-black overflow-hidden select-none"
          style={{ height: '400px' }}
        >
          {imageSrc && (
            <>
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Recortar"
                onLoad={handleImageLoad}
                className="absolute pointer-events-none"
                style={{
                  left: offsetX,
                  top: offsetY,
                  width: displaySize.w,
                  height: displaySize.h,
                  objectFit: 'contain',
                }}
                draggable={false}
              />

              {/* Overlay oscuro */}
              {crop.width > 0 && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: offsetX,
                    top: offsetY,
                    width: displaySize.w,
                    height: displaySize.h,
                  }}
                >
                  {/* Top */}
                  <div className="absolute bg-black/60" style={{ top: 0, left: 0, right: 0, height: crop.y }} />
                  {/* Bottom */}
                  <div className="absolute bg-black/60" style={{ top: crop.y + crop.height, left: 0, right: 0, bottom: 0 }} />
                  {/* Left */}
                  <div className="absolute bg-black/60" style={{ top: crop.y, left: 0, width: crop.x, height: crop.height }} />
                  {/* Right */}
                  <div className="absolute bg-black/60" style={{ top: crop.y, left: crop.x + crop.width, right: 0, height: crop.height }} />
                </div>
              )}

              {/* Recuadro de recorte */}
              {crop.width > 0 && (
                <div
                  className="absolute border-2 border-cyan-400"
                  style={{
                    left: `calc(${offsetX} + ${crop.x}px)`,
                    top: `calc(${offsetY} + ${crop.y}px)`,
                    width: crop.width,
                    height: crop.height,
                    cursor: dragging ? 'grabbing' : 'grab',
                    boxShadow: '0 0 0 1px rgba(6,182,212,0.3)',
                  }}
                  onMouseDown={(e) => handleMouseDown(e, 'drag')}
                >
                  {/* Líneas de la regla de tercios */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute border-white/20 border-dashed border-r" style={{ left: '33.33%', top: 0, bottom: 0 }} />
                    <div className="absolute border-white/20 border-dashed border-r" style={{ left: '66.66%', top: 0, bottom: 0 }} />
                    <div className="absolute border-white/20 border-dashed border-b" style={{ top: '33.33%', left: 0, right: 0 }} />
                    <div className="absolute border-white/20 border-dashed border-b" style={{ top: '66.66%', left: 0, right: 0 }} />
                  </div>

                  {/* Esquinas */}
                  {[
                    'top-0 left-0 -translate-x-1/2 -translate-y-1/2',
                    'top-0 right-0 translate-x-1/2 -translate-y-1/2',
                    'bottom-0 left-0 -translate-x-1/2 translate-y-1/2',
                  ].map((pos, i) => (
                    <div
                      key={i}
                      className={`absolute w-3 h-3 bg-cyan-400 rounded-sm pointer-events-none ${pos}`}
                    />
                  ))}

                  {/* Esquina de resize */}
                  <div
                    className="absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 w-4 h-4 bg-white rounded-sm border-2 border-cyan-400 cursor-se-resize z-10"
                    onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, 'resize'); }}
                  />
                </div>
              )}
            </>
          )}

          {!imageSrc && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Canvas oculto para exportar */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 bg-black/30">
          <div className="text-gray-500 text-xs">
            {crop.width > 0 && (
              <span>
                Recorte: {Math.round(crop.width / scale)} × {Math.round(crop.height / scale)} px
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-5 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleCrop}
              disabled={crop.width === 0}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white text-sm font-bold rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line"></i>
              Aplicar recorte
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
